import { useState, useEffect } from 'react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import type { Seller, PaymentMethod } from '../types/api';
import { CloseIcon, ImageIcon, MinusIcon, PlusIcon, ShoppingCartIcon } from './Icons';
import './CartPanel.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CartPanel({ open, onClose }: Props) {
  const { items, total, totalItems, updateQuantity, removeItem, clear } = useCart();
  const { isAuthenticated } = useAuth();
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedSeller, setSelectedSeller] = useState<string>('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loadingMeta, setLoadingMeta] = useState(false);

  useEffect(() => {
    if (open && isAuthenticated) {
      setLoadingMeta(true);
      Promise.all([api.getSellers(), api.getPaymentMethods()])
        .then(([s, payments]) => {
          setSellers(s);
          setPaymentMethods(payments);
          if (s.length > 0) setSelectedSeller(s[0].id);
          if (payments.length > 0) setSelectedPaymentMethod(payments[0].id);
        })
        .catch(() => {})
        .finally(() => setLoadingMeta(false));
    } else {
      setLoadingMeta(false);
    }
  }, [open, isAuthenticated]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    if (!orderResult) return;

    const timer = window.setTimeout(() => {
      handleClosePanel();
    }, 1400);

    return () => window.clearTimeout(timer);
  }, [orderResult]);

  if (!open) return null;

  async function handleSubmit() {
    setError('');
    setSubmitting(true);

    try {
      if (!selectedSeller) {
        throw new Error('Selecione um vendedor');
      }
      if (!selectedPaymentMethod) {
        throw new Error('Selecione uma forma de pagamento');
      }

      const res = await api.createOrder({
        sellerId: selectedSeller,
        paymentMethodId: selectedPaymentMethod,
        items: items.map(i => ({ productId: i.productId, quantity: i.quantity })),
        notes,
      });

      setOrderResult(res.id);
      clear();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao enviar pedido');
    } finally {
      setSubmitting(false);
    }
  }

  function handleClosePanel() {
    setOrderResult(null);
    setError('');
    setSubmitting(false);
    onClose();
  }

  const formatPrice = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div className="cart-overlay" onClick={handleClosePanel}>
      <div className="cart-panel" onClick={e => e.stopPropagation()}>
        <div className="cart-header">
          <div className="cart-header-title">
            <ShoppingCartIcon size={20} />
            <h2>Carrinho ({totalItems})</h2>
          </div>
          <button className="cart-close" onClick={handleClosePanel} aria-label="Fechar" type="button">
            <CloseIcon size={20} />
          </button>
        </div>

        {orderResult ? (
          <div className="cart-success">
            <div className="cart-success-icon">✓</div>
            <h3>Pedido enviado com sucesso!</h3>
            <p>Protocolo: <strong>{orderResult}</strong></p>
            <button className="cart-success-btn" onClick={handleClosePanel} type="button">Continuar comprando</button>
          </div>
        ) : loadingMeta ? (
          <div className="cart-empty">
            <ShoppingCartIcon size={26} />
            <p>Carregando carrinho...</p>
            <span>Buscando vendedores e formas de pagamento.</span>
          </div>
        ) : (
          <>
            {items.length === 0 ? (
              <div className="cart-empty">
                <ShoppingCartIcon size={26} />
                <p>Carrinho vazio</p>
                <span>Adicione itens para montar o pedido.</span>
              </div>
            ) : (
              <div className="cart-items">
                {items.map(item => (
                  <div key={item.productId} className="cart-item">
                    <div className="cart-item-thumb" aria-hidden="true">
                      <div className="cart-item-thumb-fallback">
                        <ImageIcon size={18} />
                      </div>
                    </div>

                    <div className="cart-item-info">
                      <p className="cart-item-name">{item.productName}</p>
                      <p className="cart-item-price">{formatPrice(item.unitPrice)}</p>
                    </div>

                    <div className="cart-item-qty">
                      <button className="cart-qty-btn" onClick={() => updateQuantity(item.productId, item.quantity - 1)} aria-label="Diminuir quantidade" type="button">
                        <MinusIcon size={16} />
                      </button>
                      <span>{item.quantity}</span>
                      <button className="cart-qty-btn" onClick={() => updateQuantity(item.productId, item.quantity + 1)} aria-label="Aumentar quantidade" type="button">
                        <PlusIcon size={16} />
                      </button>
                    </div>

                    <div className="cart-item-total">
                      <p>{formatPrice(item.unitPrice * item.quantity)}</p>
                      <button className="cart-item-remove" onClick={() => removeItem(item.productId)} aria-label="Remover" type="button">
                        <CloseIcon size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {items.length > 0 && (
              <div className="cart-footer">
                {isAuthenticated ? (
                  <>
                    <div className="cart-field">
                      <label>Vendedor</label>
                      <select value={selectedSeller} onChange={e => setSelectedSeller(e.target.value)}>
                        <option value="">Selecione...</option>
                        {sellers.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="cart-field">
                      <label>Forma de pagamento</label>
                      <select value={selectedPaymentMethod} onChange={e => setSelectedPaymentMethod(e.target.value)}>
                        <option value="">Selecione...</option>
                        {paymentMethods.map(pm => (
                          <option key={pm.id} value={pm.id}>{pm.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="cart-field">
                      <label>Observação</label>
                      <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Opcional" />
                    </div>
                    {error && <p className="cart-error">{error}</p>}
                    <div className="cart-total-bar">
                      <span>Total</span>
                      <strong>{formatPrice(total)}</strong>
                    </div>
                    <button className="cart-checkout" onClick={handleSubmit} disabled={submitting} type="button">
                      {submitting ? 'Enviando...' : 'Finalizar pedido'}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="cart-total-bar">
                      <span>Total</span>
                      <strong>{formatPrice(total)}</strong>
                    </div>
                    <p className="cart-login-msg">Faça login para finalizar o pedido.</p>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
